import { useEffect, useState } from 'react';
import { AppShell } from '../components/AppShell';
import { api, type MeStats } from '../lib/api';
import { useSession } from '../hooks/useSession';

export default function StatsPage() {
  const user = useSession(s => s.user);
  const [stats, setStats] = useState<MeStats | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.myStats().then(setStats).catch(e => setErr(e.message));
  }, []);

  return (
    <AppShell>
      <h1 className="font-display text-2xl font-bold mb-4">Your statistics</h1>
      {user?.isGuest && (
        <div className="panel p-3 mb-4 text-sm text-ink-200 flex items-start gap-2">
          <span>👤</span>
          <span>
            You're playing as a guest — your stats are tracked locally to this account but
            won't appear on the public leaderboard.{' '}
            <a href="/auth/google" className="text-accent underline">Sign in with Google</a> to be ranked.
          </span>
        </div>
      )}
      {err && <div className="text-rose-400 mb-3">{err}</div>}
      {!stats ? <div className="text-ink-200 animate-pulse">Loading…</div> : (
        <div className="space-y-4">
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
            <Stat label="Games" value={stats.gamesPlayed} />
            <Stat label="Wins" value={stats.wins} accent />
            <Stat label="Losses (Durak)" value={stats.losses} />
            <Stat label="Win rate" value={`${Math.round(stats.winRate * 100)}%`} />
            <Stat label="Draws" value={stats.draws} />
            <Stat label="Current streak" value={stats.currentWinStreak} />
            <Stat label="Longest streak" value={stats.longestWinStreak} accent />
          </div>
          <section className="panel p-4">
            <h2 className="section-label mb-2">Recent games</h2>
            {stats.recent.length === 0 && <div className="text-ink-300 text-sm">No games played yet</div>}
            <ul className="divide-y divide-ink-700/60">
              {stats.recent.map(g => (
                <li key={g.gameId} className="py-2 flex items-center justify-between text-sm">
                  <div>{new Date(g.endedAt || g.startedAt).toLocaleString()}</div>
                  <div className={g.isDurak ? 'text-rose-400' : g.finishOrder === 0 ? 'text-emerald-400' : 'text-ink-100'}>
                    {g.isDurak ? 'Durak' : g.finishOrder === 0 ? '1st out' : `${ordinal(g.finishOrder + 1)} out`}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </div>
      )}
    </AppShell>
  );
}

function ordinal(n: number) {
  const s = ['th','st','nd','rd'], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function Stat({ label, value, accent }: { label: string; value: string | number; accent?: boolean }) {
  return (
    <div className="panel p-4">
      <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-200">{label}</div>
      <div className={`text-2xl font-display font-bold mt-1 ${accent ? 'text-accent' : 'text-ink-50'}`}>{value}</div>
    </div>
  );
}
